<template>
    <div class="sunburst-chart">
        <div id="popup" style="display: none;" v-tippy-html>
            <div v-if="hoveredNode" class="popover-content">
                <h3>{{ hoveredNode.label }}</h3>
                <h4>Presupuesto ejecutado</h4>
                <div class="amount">
                    {{ moneyFormatter(hoveredNode[this.metric]) }}
                </div>
                <div class="percentage">
                    {{ numeral(hoveredNode.ratio).format('0.00%') }} del total
                </div>
            </div>
        </div>
        <svg
            :width="width"
            :height="height"
            :viewBox="'0 0 ' + width + ' ' + height"
        >
            <g :transform="'translate(' + margin.left + ',' + margin.top + ')'">
                <g
                    v-if="selectedNode"
                    :transform="
                        'translate(' +
                            chartWidth / 2 +
                            ',' +
                            chartHeight / 2 +
                            ')'
                    "
                >
                    <!--<text text-anchor="middle" y="-10">
                        {{ selectedNode.label }}
                    </text>
                    <text text-anchor="middle" y="20">
                        {{ moneyFormatter(selectedNode.sum) }}
                    </text>-->
                    <foreignObject
                        :x="-innerRadius"
                        :y="-innerRadius"
                        :width="2 * innerRadius"
                        :height="2 * innerRadius"
                    >
                        <div class="info">
                            <div class="label">
                                {{ selectedNode.label }}
                            </div>
                            <div class="metric">
                                {{ moneyFormatter(selectedNode.sum) }}
                            </div>
                        </div>
                    </foreignObject>

                    <g
                        v-for="d in pieData"
                        :key="d.data.key"
                        class="arc"
                        :fill="d.data.color"
                        :opacity="d.data.opacity"
                        @click="selectNode(d.data)"
                        @mouseenter="hoveredNode = d.data"
                        v-tippy="{
                            html: '#popup',
                            followCursor: true,
                            arrow: true,
                            animation: 'fade'
                        }"
                    >
                        <path :d="path(d)"></path>
                    </g>
                    <g
                        v-for="d in outerPieData"
                        :key="d.data.key"
                        class="arc"
                        :fill="d.data.color"
                        :opacity="d.data.opacity"
                        @mouseenter="hoveredNode = d.data"
                        v-tippy="{
                            html: '#popup',
                            followCursor: true,
                            arrow: true,
                            animation: 'fade'
                        }"
                    >
                        <path :d="outerPath(d)"></path>
                    </g>
                </g>
            </g>
        </svg>
    </div>
</template>
<style lang="scss">
.sunburst-chart {
    svg {
        width: 100%;
        height: auto;
        .arc {
            cursor: pointer;
        }
        .info {
            display: flex;
            width: 100%;
            height: 100%;
            justify-content: center;
            flex-direction: column;
            text-align: center;
            padding: 10%;
            .label {
                font-size: 16px;
            }
            .metric {
                font-size: 24px;
            }
        }
    }
}
</style>
<script>
import _ from 'lodash';
import * as d3 from 'd3';
import moneyFormatter from '../../Methods/MoneyFormatter';
import numeral from 'numeral';

export default {
    props: {
        data: { required: true, type: Array },
        dimensions: { required: true, type: Object },
        metric: { default: 'sum', type: String },
        labels: { default: null, type: Object },
        breadcrumb: { default: [], type: Array }
    },
    data() {
        return {
            width: 600,
            height: 600,
            margin: {
                left: 0,
                top: 0,
                right: 0,
                bottom: 0
            },
            hoveredNode: null,
            selectedNode: null
        };
    },
    computed: {
        computedData() {
            return {
                type: 'País',
                label: 'Chile',
                sum: _.sumBy(this.data, this.metric),
                ratio: 1,
                children: this.createNode(this.data, this.dimensions.ordinal)
            };
        },
        chartWidth: function() {
            return this.width - this.margin.left - this.margin.right;
        },
        chartHeight: function() {
            return this.height - this.margin.top - this.margin.bottom;
        },
        radius: function() {
            return Math.min(this.chartWidth, this.chartHeight) / 2;
        },
        innerRadius: function() {
            return this.radius - 140;
        },
        outerRadius: function() {
            return this.radius - 20;
        },
        pie: function() {
            return d3
                .pie()
                .padAngle(0.01)
                .sort(null)
                .value(d => {
                    return d[this.metric];
                });
        },
        path: function() {
            return d3
                .arc()
                .outerRadius(this.outerRadius)
                .innerRadius(this.innerRadius);
        },
        color: function() {
            return d3.scaleOrdinal([
                '#081D58',
                '#1D91C0',
                '#7A0177',
                '#AE017E',
                'red'
            ]);
        },
        pieData() {
            if (!this.selectedNode) return;

            return this.pie(this.selectedNode.children);
        },
        outerPieComputedData() {
            const total = _.sumBy(this.data, this.metric);
            return [
                {
                    label: this.selectedNode.label,
                    sum: this.selectedNode.sum,
                    ratio: this.selectedNode.sum / total,
                    color: this.selectedNode.color,
                    opacity: 0.8
                },
                {
                    label: 'Restante',
                    sum: total - this.selectedNode.sum,
                    ratio: (total - this.selectedNode.sum) / total,
                    color: this.selectedNode.color,
                    opacity: 0.6
                }
            ];
            return this.selectedNode.children;
        },
        outerPath: function() {
            return d3
                .arc()
                .outerRadius(this.radius)
                .innerRadius(this.radius - 18);
        },
        outerPieData() {
            if (!this.outerPieComputedData) return;

            return this.pie(this.outerPieComputedData);
        }
    },
    watch: {
        data() {
            this.selectNode(this.computedData);
        }
    },
    methods: {
        moneyFormatter,
        numeral,
        selectNode(node) {
            if (!node.children) {
                this.$emit('detail-clicked', node);
                return;
            }

            this.selectedNode = node;
            this.breadcrumb.push(this.selectedNode);
        },
        createNode(data, dimensions, parentId = null) {
            if (dimensions.length === 0) return;

            const total = _.sumBy(data, this.metric);

            return _(data)
                .groupBy(dimensions[0])
                .map((d, key) => {
                    const sum = _.sumBy(d, this.metric);
                    const id = parentId ? `${parentId}/${key}` : key;
                    return {
                        id,
                        key,
                        type: dimensions[0],
                        label: _.get(
                            this.labels,
                            dimensions[0] + '.' + key,
                            d[0][`nombre_${dimensions[0]}`]
                        ),
                        sum,
                        ratio: sum / total,
                        count: d.length,
                        color: this.color(key),
                        opacity: 1,
                        children: this.createNode(d, dimensions.slice(1), id)
                    };
                })
                .value();
        },
        pop: function() {
            if (this.breadcrumb.length <= 1) return;

            this.breadcrumb.pop();
            this.selectedNode = this.breadcrumb[this.breadcrumb.length - 1];
        }
    }
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/Components/Charts/Sunburst.vue